import React, { useState, useEffect, useRef } from 'react';
import {
	Box,
	Breadcrumbs,
	Button,
	Grid,
	MenuItem,
	Stack,
	TextField,
	Tooltip,
	Typography
} from '@mui/material';
import PageHeader from 'components/Header/Page';
import moment from 'moment';
import { CustomizedDataGrid } from '../customized_data_grid';
import ProgressHorizontalBarChart from 'components/Widgets/ProgressHorizontalBarChart';
import StackedBarLineTrendChart from 'components/Widgets/StackedBarLineTrendChart';
import {
	exportTopStorageAccountsList,
	fetchStorageAccountsDetails,
	fetchTopStorageAccountsList,
	getContainerGrowthTrend,
	getContainerOptions,
	getProjectOptions,
	getStorageAccountsOptions,
	getStorageBySector,
	getStorageGrowthTrend
} from 'service/service';
import AzureStorageIcon from '../../assets/Azure Storage.svg';
import SectorCheckFilter from 'components/Dropdown/SectorCheckFilter';
import DoughnutChartWidget from 'components/Widgets/DoughnutChartWidget';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RowTypography from 'components/Table/RowTypography';
import LoaderComponent from 'components/Loader';
import MultiCheckFilter from 'components/Dropdown/MultiCheckFilter';
import CustomDropdown from 'components/Dropdown';
import { useTheme } from "@mui/material/styles";
import PageWrapper from 'components/Common/PageWraper';
import HighlightedText from 'components/GlobalSearch/HighlightText';

type StorageUnit = 'GB' | 'TB' | 'PB';

const azureStorage = () => {
	interface Row {
		storageAccount: string;
		resourceGroup: string;
		size: string;
		monthlyCost: string;
		growth: string;
		owner: string;
		lastModified: any;
		containerName: any;
		parentFolderName: any;
		name: any;
	}
	const theme = useTheme();
	const [chartView, setChartView] = useState(true);
	const [activeTab, setActiveTab] = useState<string>('Chart');
	const [growthTrendData, setGrowthTrendData] = useState<any[]>([]);
	const [topStorageAccountsData, setTopStorageAccountsData] = useState<any[]>([]);
	const [growthChartLoading, setGrowthChartLoading] = useState<boolean>(true);
	const [topStorageChartLoading, settopStorageChartLoading] = useState<boolean>(false);
	const [allStorageAccounts, setAllStorageAccounts] = useState<any>([])
	const [loading, setLoading] = useState(false);
	const [tableLoading, setTableLoading] = useState<boolean>(false);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(50);
	const [totalPages, setTotalPages] = useState(0);
	const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
	const [sortingInfo, setSortingInfo] = useState({ sortBy: 'size', sortDir: 'desc' });
	const [storageAccountName, setStorageAccountName] = useState('');
	const [containerName, setContainerName] = useState('');
	const [storageOptions, setStorageOptions] = useState<string[]>([]);
	const [projectOptions, setProjectOptions] = useState<string[]>([]);
	const [availableContainers, setAvailableContainers] = useState<string[]>([]);
	const [loadingContainers, setLoadingContainers] = useState(false);
	const [loadingStorageAccounts, setLoadingStorageAccounts] = useState(false);
	const [selectedAccount, setSelectedAccount] = useState<string>('');
	const [selectedContainer, setSelectedContainer] = useState<string>('');
	const [selectedFolderName, setSelectedFolderName] = useState('');
	const [cache, setCache] = useState<any>({});
	const [breadcrumbPath, setBreadcrumbPath] = useState<string[]>([]);
	const [tableDataList, setTableDataList] = useState<any[]>([]);
	const [isDoughnutLoading, setIsDoughnutLoading] = useState<boolean>(false);
	const [storageBySectorsData, setstorageBySectorsData] = useState<any[]>([]);
	const [totalStorage, setTotalStorage] = useState<string>('');
	const checkedListRef = useRef<any>([]);
	const ProjectcheckedListRef = useRef<string[]>([]);
	const [projectFilter, setProjectFilter] = useState<string[]>([]);
	const [sectorFilter, setSectorFilter] = useState<string[]>([]);
	const [conversionUnit, setConversionUnit] = useState<StorageUnit>('TB');
	const [rawTableData, setRawTableData] = useState<any[]>([]);
	const [rawSectorData, setRawSectorData] = useState<any[]>([]);
	const [rawGrowthTrendData, setRawGrowthTrendData] = useState<any[]>([]);

	const handleByteChange = (e: any) => {
		const unit = e.target.value;
		setConversionUnit(unit);
		setstorageBySectorsData(transformSectorStorage(rawSectorData, unit));
		setGrowthTrendData(convertStorageGrowthData(rawGrowthTrendData, unit));
		setTopStorageAccountsData(transformData(rawTableData, unit));
	};

	const sectorColors: Record<string, string> = {
		'S&T': '#00B8D9',
		'SnT': '#00B8D9',
		'S&T,EU': '#FF4500',
		LATAM: '#FF6F61',
		EU: '#F4B400',
		CGF: '#6918ED',
		PFNA: '#00D08D',
		PBNA: '#299BFF',
		'PBNA,PFNA': '#DA70D6',
		'PBNA,PFNA,CGF': '#7B68EE',
		'PFNA,CGF': '#B22222',
		'AMESA/APAC': '#228B22',
		'AMESA': '#123acd',
		'Overall Growth': '#A0522D'
	};

	const getChartTitle = () => {
		if (breadcrumbPath.length === 0) return 'Top Storage Accounts';
		if (breadcrumbPath.length === 1) return 'Top Containers';
		return `Top Folders in Container - ${breadcrumbPath[1] || ''}`;
	};

	const defaultColumnNames = [
		'Storage Account',
		'Container Name',
		'Folder Name',
		'Blobs Count',
		'Sector Name',
		'Size (GB)',
		'Size (TB)',
		'Size (PB)',
		'Project',
		'Last Modified (UTC)',
		'Owner'
	];
	const extraColumns = [
		{ key: 'subscription', label: 'Subscription', saOnly: false },
		{ key: 'resourceGroup', label: 'Resource Group', saOnly: false },
		{ key: 'owner', label: 'Owner', saOnly: true },
		{ key: 'cost', label: 'Cost (USD)', saOnly: false },
		{ key: 'governance1', label: 'Governance1', saOnly: false },
		{ key: 'governance2', label: 'Governance2', saOnly: false },
		{ key: 'tables', label: 'Tables', saOnly: true },
		{ key: 'queues', label: 'Queues', saOnly: true },
		{ key: 'tags', label: 'Tags', saOnly: true },
		{ key: 'eTag', label: 'eTag', saOnly: false },
		{ key: 'accessTier', label: 'Access Tier', saOnly: false },
		{ key: 'permissions', label: 'Permissions', saOnly: false },
		{ key: 'acl', label: 'ACL', saOnly: false },
		{ key: 'lastAccessTime', label: 'Last Accessed Time (UTC)', saOnly: false },
	];
	defaultColumnNames.push(...extraColumns.map((c) => c.label));

	const handleProjectFilterApply = () => {
		setProjectFilter(ProjectcheckedListRef.current);
		fetchGrowthTrendData(breadcrumbPath, ProjectcheckedListRef.current, sectorFilter)
		fetchTableData(breadcrumbPath, 1, pageSize, ProjectcheckedListRef.current, sectorFilter, sortingInfo);
	};

	const handleStorageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const selectedAccount = e.target.value;
		setStorageAccountName(selectedAccount);
		setContainerName('');
		setAvailableContainers([]);

		if (!selectedAccount) return;

		await fetchConatinerOptionsByStorageAccount(selectedAccount);
	};

	function getColumns(): Array<any> {
		const hasContainerName = tableDataList.some((item) => item.containerName && item.containerName.trim() !== '');
		const hasFolderName = !hasContainerName && tableDataList.some((item) => item.name && item.name.trim() !== '');

		let primaryKey = 'storageAccountName';
		let primaryLabel = 'Storage Account';

		if (hasContainerName) {
			primaryKey = 'containerName';
			primaryLabel = 'Container Name';
		}
		if (hasFolderName) {
			primaryKey = 'name';
			primaryLabel = 'Folder Name';
		}

		const hasProject = tableDataList.some((item) => item.project);
		const hasLastModified = tableDataList.some((item) => item.lastModified);

		const columns = [
			{
				key: primaryKey,
				name: primaryLabel,
				sortable: true,
				locked: true,
				formatter: (row: any) => {
					const isClickable =
						row.row?.isFile !== 1 &&
						(typeof row.row?.level !== 'number' || row.row.level < 5) &&
						row.row?.lastLevel !== true;

					const handleClick = async () => {
						if (!isClickable) return;

						// Update breadcrumbPath allowing duplicates
						const newBreadcrumb = [...breadcrumbPath, row.row[primaryKey]];
						setBreadcrumbPath(newBreadcrumb);

						const cacheKey = newBreadcrumb.join('/');
						if (cache[cacheKey]) {
							const cached = cache[cacheKey];
							setTopStorageAccountsData(transformData(cached.data, conversionUnit)); // Chart data
							setTableDataList(cached.data ?? []);
							setPaginationData({
								totalElements: cached?.pagination?.totalRecords,
								totalPages: cached?.pagination?.totalPages
							});
							setPage(cached?.pagination?.currentPage);
							setPageSize(cached?.pagination?.pageSize);
							setTotalPages(cached?.pagination?.totalPages);
							fetchGrowthTrendData(newBreadcrumb, projectFilter, sectorFilter);
						} else {
							fetchGrowthTrendData(newBreadcrumb, projectFilter, sectorFilter);
							await fetchTableData(newBreadcrumb, page, pageSize, projectFilter, sectorFilter, sortingInfo);
						}
					};

					return (
						<RowTypography
							onClick={isClickable ? handleClick : undefined}
							sx={{ cursor: isClickable ? 'pointer' : 'default', opacity: isClickable ? 1 : 0.5 }}
						>
							{row.row?.lastLevel !== true ? (
								row.row[primaryKey] || '-'
							) : (
								<Tooltip title="No Subfolders">
									<span>{row.row[primaryKey] || '-'}</span>
								</Tooltip>
							)}
						</RowTypography>
					);
				}
			},
			{
				key: 'count',
				name: 'Blobs Count',
				sortable: true,
				formatter: (row: any) => (
					<RowTypography>
						{row.row.count ?? '-'}
					</RowTypography>
				),
			},
			{
				key: 'size',
				name: `Size (${conversionUnit})`,
				sortable: true,
				formatter: (row: any) => {
					return <RowTypography>{convertBytes(Number(row.row.size), conversionUnit)}</RowTypography>;
				}
			},
			...(breadcrumbPath.length < 1
				? [
					{
						key: 'sector',
						name: 'Sector Name',
						sortable: true,
						formatter: (row: any) => {
							const sectorList: string[] = row.row.sector || [];
							const display = sectorList.length ? sectorList.join(', ') : '-';
							return <RowTypography>{display}</RowTypography>;
						}
					}
				]
				: [])
		];
		// Add Project column only for account or container level
		if (hasProject) {
			columns.push({
				key: 'project',
				name: 'Project',
				sortable: true,
				formatter: (row: any) => {
					const projectList: string[] = row.row.project || [];
					const display = projectList.length ? projectList.join(', ') : '-';
					return <RowTypography>{display}</RowTypography>;
				}
			});
		}
		if (hasLastModified) {
			columns.push({
				key: 'lastModified',
				name: 'Last Modified (UTC)',
				sortable: true,
				formatter: (row: any) => {
					const formatted = moment.utc(row.row.lastModified).format('YYYY-MM-DD HH:mm:ss');
					return <RowTypography>{formatted}</RowTypography>;
				}
			});
		}
		for (const col of extraColumns) {
			if (breadcrumbPath.length < 1 && col.key === 'eTag') {
				continue
			}
			if (breadcrumbPath.length >= 1 && col.saOnly) {
				continue;
			}
			columns.push({
				key: col.key,
				name: col.label,
				sortable: false,
				formatter: (row: any) => {
					if (col.key === 'lastAccessTime') {
						const lastAccessTime = row.row[col.key];
						return <RowTypography>{lastAccessTime ? moment.utc(lastAccessTime).format('YYYY-MM-DD HH:mm:ss') : '-'}</RowTypography>;
					}
					if (col.key === 'cost') {
						const detailedCost = row.row['detailedCost'] || null
						if (!detailedCost) return <RowTypography>{row.row[col.key]}</RowTypography>;

						return (
							<Tooltip
								title={
									<pre style={{ margin: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
										{
											detailedCost?.map((dc: any) => <>
												<p>Project: {dc?.ProjectName}</p>
												<p>Business Application: {dc?.businessapplicationname || ' - '}</p>
												<p>Cost: {dc.ytd_cost}</p>
												<br />
											</>)
										}
									</pre>
								}
								arrow
								PopperProps={{
									popperOptions: {
										modifiers: [
											{
												name: 'preventOverflow',
												enabled: false,
											},
										],
									},
									sx: {
										'.MuiTooltip-tooltip': {
											maxWidth: 'none',
										},
									},
								}}
							>
								<Box component="span" sx={{ cursor: 'pointer' }}>
									<RowTypography>{row.row[col.key]}</RowTypography>
								</Box>
							</Tooltip>
						)
					}
					if (col.key === 'tags') {
						const tags = row.row[col.key] || {};
						return (
							<Tooltip
								title={
									<pre style={{ margin: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
										{JSON.stringify(tags, null, 2)}
									</pre>
								}
								arrow
								PopperProps={{
									popperOptions: {
										modifiers: [
											{
												name: 'preventOverflow',
												enabled: false,
											},
										],
									},
									sx: {
										'.MuiTooltip-tooltip': {
											maxWidth: 'none',
										},
									},
								}}
							>
								<Box component="span" sx={{ cursor: 'pointer' }}>
									<RowTypography>{Object.keys(tags).length > 0 ? 'View Tags' : '-'}</RowTypography>
								</Box>
							</Tooltip>
						);
					}
					return <RowTypography>{row.row[col.key]}</RowTypography>;
				}
			});
		}
		return columns.map((col) => ({
			...col,
			minWidth: 200,
		}));
	}

	const isRunQueryEnabled = storageAccountName.trim() !== '';

	const isClearEnabled =
		(storageAccountName && storageAccountName.trim() !== '') || (containerName && containerName.trim() !== '');

	const getSubscriptionName = (storageAccountName: string) => allStorageAccounts?.find((sa: any) => sa.storageAccountName === storageAccountName)?.subscription || ''
	const getResourceGroupName = (storageAccountName: string) => allStorageAccounts?.find((sa: any) => sa.storageAccountName === storageAccountName)?.resourceGroup || ''

	const handleSorting = (newSortingInfo: any) => {
		setSortingInfo(newSortingInfo);
		fetchTableData(breadcrumbPath, page, pageSize, projectFilter, sectorFilter, newSortingInfo);
	};

	const handlepageSizeChange = (event: any) => {
		const value = event.target.value;
		fetchTableData(breadcrumbPath, 1, value, projectFilter, sectorFilter, sortingInfo);
	};

	const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
		fetchTableData(breadcrumbPath, value, pageSize, projectFilter, sectorFilter, sortingInfo);
	};

	const fetchTableData = async (
		breadcrumb: string[],
		page: number,
		pageSize: number,
		projectFilters: string[],
		sectorFilters: string[],
		sortingInfo?: any
	) => {
		try {
			setTableLoading(true);
			settopStorageChartLoading(true);

			const storageAccountName = breadcrumb[0] || '';
			const path = breadcrumb.slice(1).join('/');

			const payload: any = {
				storageAccountName,
				path,
				page,
				limit: pageSize,
				sortBy: sortingInfo?.sortBy,
				sortOrder: sortingInfo?.sortDir,
				filterBy: [{ project: projectFilters }, { sector: sectorFilters }]
			};

			// Only pass `level` if path is non-empty
			if (breadcrumb.length >= 1) {
				payload.level = breadcrumb.length;
			}

			const response = await fetchTopStorageAccountsList(payload);

			if (response?.data) {
				const newData = response.data.data ?? [];
				newData.forEach((element: any) => {
					element.subscription = element.subscription || getSubscriptionName(storageAccountName)
					element.resourceGroup = element.resourceGroup || getResourceGroupName(storageAccountName)
				});
				setRawTableData([...newData]);
				setTableDataList([...newData]);
				setPaginationData({
					totalElements: response?.data?.pagination?.totalRecords,
					totalPages: response?.data?.pagination?.totalPages
				});
				setPage(response?.data?.pagination?.currentPage);
				setPageSize(response?.data?.pagination?.pageSize);
				setTotalPages(response?.data?.pagination?.totalPages);

				// Cache data using breadcrumb as key
				const breadcrumbKey = breadcrumb.length === 0 ? 'topAccounts' : breadcrumb.join('/');
				setCache((prev: any) => ({
					...prev,
					[breadcrumbKey]: response.data
				}));

				const result = transformData(response.data.data, conversionUnit);
				setTopStorageAccountsData(result);
			} else {
				console.error('No data returned in response');
			}
		} catch (error) {
			console.error('Error fetching table data:', error);
		} finally {
			setTableLoading(false);
			settopStorageChartLoading(false);
		}
	};

	const exportTableData = async (
		breadcrumb: string[],
		page: number,
		pageSize: number,
		projectFilters: string[],
		sectorFilters: string[],
		conversionUnit: StorageUnit,
		sortingInfo?: any
	) => {
		try {
			const storageAccountName = breadcrumb[0] || '';
			const path = breadcrumb.slice(1).join('/');

			const payload: any = {
				storageAccountName,
				path,
				page,
				limit: pageSize,
				sortBy: sortingInfo?.sortBy,
				sortOrder: sortingInfo?.sortDir,
				filterBy: [{ project: projectFilters }, { sector: sectorFilters }],
				storageUnit: conversionUnit
			};

			// Only pass `level` if path is non-empty
			if (breadcrumb.length >= 2) {
				payload.level = breadcrumb.length - 1;
			}

			await exportTopStorageAccountsList(payload);
		} catch (error) {
			console.error('Error exporting table data:', error);
		}
	};

	const clearFilters = () => {
		setStorageAccountName('');
		setContainerName('');
		setBreadcrumbPath([]);
		fetchTableData([], page, pageSize, projectFilter, sectorFilter, sortingInfo);
	};

	useEffect(() => {
		fetchChartData();
		getStorageAccountsDetails();
		fetchGrowthTrendData(breadcrumbPath, projectFilter, sectorFilter);
		fetchTableData(breadcrumbPath, page, pageSize, projectFilter, sectorFilter, sortingInfo);
	}, []);

	const handleTabClick = (view: 'Chart' | 'Table') => {
		setActiveTab(view);
		setChartView(view === 'Chart');
	};

	const convertBytes = (bytes: number, unit: StorageUnit): string => {
		const unitMap = {
			GB: 1024 ** 3,
			TB: 1024 ** 4,
			PB: 1024 ** 5
		};

		const divisor = unitMap[unit];
		return (bytes / divisor).toFixed(2);
	};

	const transformData = (data: any[], unit: StorageUnit) => {
		return data
			.filter((a) => !a.sector || !a.sector.includes('Unknown'))
			.sort((a, b) => b.size - a.size)
			.map((item) => ({
				name: item.name || item.containerName || item.storageAccountName,
				value: convertBytes(item.size, unit),
				valueInBytes: item.size,
				unit: unit,
				item
			}));
	};

	const transformSectorStorage = (storageSectorRes: any, unit: StorageUnit) => {
		const sectors = storageSectorRes.sectors || [];
		const totalBytes = Number(storageSectorRes?.totalStorageBytes || 0);
		setTotalStorage(`${convertBytes(totalBytes, unit)} ${unit}`);
		return sectors.map((sector: any) => {
			const color = sectorColors[sector.sector] || '#AABBCC'
			const obj = {
				name: sector.sector,
				value: Number(convertBytes(Number(sector.totalSizeBytes), unit)),
				unit: unit,
				itemStyle: { color }
			}
			return obj
		});
	};

	function convertStorageGrowthData(response: any, unit: StorageUnit) {
		if (!response || !Array.isArray(response)) return [];

		return response.map((entry: any) => {
			const newEntry: any = { month: entry.month, unit };

			for (const key in entry) {
				if (key === 'month' || key === 'overallGrowthPercentage') {
					newEntry[key] = entry[key];
				} else {
					const bytes = Number(entry[key]);
					const converted = Number(convertBytes(bytes, unit));
					newEntry[key === 'totalSizeInBytes' ? `totalSizeInBytes` : key] = converted;
				}
			}
			return newEntry;
		});
	}

	async function fetchChartData() {
		try {
			setGrowthChartLoading(true);
			settopStorageChartLoading(true);
			setIsDoughnutLoading(true);
			setLoadingStorageAccounts(true);

			const [
				storageBySectorRes,
				storageAccoutOptions,
				projectOptions
			] = await Promise.all([
				getStorageBySector(),
				getStorageAccountsOptions(),
				getProjectOptions()
			]);

			if (storageBySectorRes.success) {
				const storageSectorRes = storageBySectorRes.data ?? [];
				setRawSectorData(storageSectorRes);
				const result = transformSectorStorage(storageSectorRes, conversionUnit);
				setstorageBySectorsData(result);
			}

			if (storageAccoutOptions.success) {
				setStorageOptions(storageAccoutOptions?.data?.storageAccounts || []);
			}
			if (projectOptions.success) {
				setProjectOptions(projectOptions?.data?.projects?.filter((p: string) => p !== 'Unknown') || []);
			}
		} catch (error) {
			console.error(error);
		} finally {
			settopStorageChartLoading(false);
			setIsDoughnutLoading(false);
			setLoadingStorageAccounts(false);
		}
	}

	const getStorageAccountsDetails = async () => {
		try {
			const allAccounts = await fetchStorageAccountsDetails()
			if (allAccounts.success) {
				setAllStorageAccounts(allAccounts?.data?.storageAccounts)
			}
		} catch (error) {
			console.error('Error fetching getStorageAccountsDetails data:', error);
		}
	}

	const fetchGrowthTrendData = async (breadcrumb: any, projectFilter: any = null, sectorFilter: any = null) => {
		try {
			setGrowthChartLoading(true);
			const storageAccountName = breadcrumb[0] || '';
			const path = breadcrumb.slice(1).join('/');

			let growthRes;

			if (breadcrumb.length === 0) {
				growthRes = await getStorageGrowthTrend({ projectFilter, sectorFilter });
			} else if (breadcrumb.length === 1) {
				growthRes = await getContainerGrowthTrend({ storageAccountName, path, type: 'container' });
			} else if (breadcrumb.length > 1) {
				growthRes = await getContainerGrowthTrend({ storageAccountName, path, type: 'folder' });
			}

			if (growthRes.success) {
				setRawGrowthTrendData(growthRes.data);
				const result = convertStorageGrowthData(growthRes.data, conversionUnit);
				setGrowthTrendData(result);
			}
		} catch (error) {
			console.error('Error fetching growth trend data:', error);
		} finally {
			setGrowthChartLoading(false);
		}
	};

	const fetchConatinerOptionsByStorageAccount = async (selectedAccount: string) => {
		try {
			setLoadingContainers(true);

			const response = await getContainerOptions(selectedAccount);

			if (response?.data) {
				const newData = response?.data?.containers ?? [];
				setAvailableContainers(newData);
			} else {
				console.error('No data returned in response');
			}
		} catch (error) {
			console.error('Error fetching table data:', error);
		} finally {
			setLoadingContainers(false);
		}
	};

	const handleBarClick = async (clickedName: string) => {
		if (breadcrumbPath.length > 5) return;
		// Update breadcrumbPath allowing duplicates
		const updatedBreadcrumb = [...breadcrumbPath, clickedName];
		setBreadcrumbPath(updatedBreadcrumb);

		const cacheKey = updatedBreadcrumb.join('/');

		// Use cached data if available
		if (cache[cacheKey]) {
			const cached = cache[cacheKey];

			setTopStorageAccountsData(transformData(cached.data, conversionUnit)); // Chart data
			setTableDataList(cached.data ?? []);
			setPaginationData({
				totalElements: cached?.pagination?.totalRecords,
				totalPages: cached?.pagination?.totalPages
			});
			setPage(cached?.pagination?.currentPage);
			setPageSize(cached?.pagination?.pageSize);
			setTotalPages(cached?.pagination?.totalPages);
			fetchGrowthTrendData(updatedBreadcrumb);
			return;
		}

		// Fetch and cache via single API
		settopStorageChartLoading(true);
		try {
			fetchGrowthTrendData(updatedBreadcrumb);
			await fetchTableData(updatedBreadcrumb, 1, pageSize, projectFilter, sectorFilter, sortingInfo);
		} catch (err) {
			console.error('Bar click fetch failed:', err);
		} finally {
			settopStorageChartLoading(false);
		}
	};

	const handleOkClick = () => {
		const updatedCheckedList = [...checkedListRef.current];
		setSectorFilter(updatedCheckedList);
		fetchGrowthTrendData(breadcrumbPath, projectFilter, updatedCheckedList)
		fetchTableData(breadcrumbPath, 1, pageSize, projectFilter, updatedCheckedList, sortingInfo);
	};

	const handleRunQueryClick = () => {
		if (!storageAccountName) return;

		setSelectedAccount(storageAccountName);
		const newBreadcrumb: string[] = [storageAccountName];
		if (containerName) newBreadcrumb.push(containerName);

		setBreadcrumbPath(newBreadcrumb);

		fetchTableData(newBreadcrumb, 1, pageSize, projectFilter, sectorFilter, sortingInfo);
	};

	interface DataObject {
		month: string;
		totalSizeInBytes: number;
		overallGrowthPercentage: number;
		[key: string]: any;
	}

	useEffect(() => {
		getProjectOptions(sectorFilter.join(',')).then((projectOptions) => setProjectOptions(projectOptions?.data?.projects?.filter((p: string) => p !== 'Unknown') || []))
	}, [sectorFilter]);

	return (
		<PageWrapper>
			<PageHeader title="Azure Storage" icon={AzureStorageIcon}>
				<CustomDropdown
					value={conversionUnit}
					handleChange={handleByteChange}
					options={[
						{ value: 'GB', label: 'GB' },
						{ value: 'TB', label: 'TB' },
						{ value: 'PB', label: 'PB' }
					]}
					boxProps={{ sx: { height: 48 } }}
				/>
				<div className="sector-filter-stack">
					<SectorCheckFilter
						sectorStorageName="ml-summary-sector-filter"
						handleOkClick={handleOkClick}
						checkedListRef={checkedListRef}
						azureStorage={true}
						disable={breadcrumbPath.length > 0}
					/>
				</div>
				<MultiCheckFilter
					title="Projects"
					options={projectOptions}
					storageKey="asm_project_filters"
					checkedListRef={ProjectcheckedListRef}
					handleOkClick={handleProjectFilterApply}
					disable={breadcrumbPath.length > 0}
				/>
			</PageHeader>
			{breadcrumbPath.length > 0 && (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						mt: -1,
						p: 2,
						px: { xs: 1, md: 2, lg: 3 },
						color: 'white',
						position: 'fixed',
						zIndex: 1000,
						width: '100%',
						backgroundColor: '#003771'
					}}
				>
					<Breadcrumbs
						separator={<ChevronRightIcon sx={{ color: 'white !important' }} />}
						aria-label="breadcrumb"
						sx={{ color: 'white !important', maxWidth: '100%' }}
					>
						<Typography
							sx={{
								cursor: 'pointer',
								color: 'white !important',
								fontWeight: 500,
								'&:hover': { color: '#90CAF9' }
							}}
							onClick={() => {
								const cacheKey = 'topAccounts'; // predefined top level cache key
								setBreadcrumbPath([]);
								setStorageAccountName('');
								setContainerName('');
								setSelectedAccount('');
								setSelectedContainer('');
								setSelectedFolderName('');

								const cached = cache[cacheKey];
								if (cached) {
									setTopStorageAccountsData(transformData(cached.data, conversionUnit));
									setRawTableData(cached.data ?? []);
									setTableDataList(cached.data ?? []);
									setPaginationData({
										totalElements: cached?.pagination?.totalRecords,
										totalPages: cached?.pagination?.totalPages
									});
									setPage(cached?.pagination?.currentPage);
									setPageSize(cached?.pagination?.pageSize);
									setTotalPages(cached?.pagination?.totalPages);
									fetchGrowthTrendData([], projectFilter, sectorFilter);
								} else {
									fetchTableData([], 1, pageSize, projectFilter, sectorFilter, sortingInfo);
									fetchGrowthTrendData([], projectFilter, sectorFilter);
								}
							}}
						>
							Storage Account
						</Typography>

						{/* Drilldown Levels */}
						{breadcrumbPath.map((label, idx) => {
							const isLast = idx === breadcrumbPath.length - 1;
							const newPath = breadcrumbPath.slice(0, idx + 1);
							const cacheKey = newPath.join('/');

							return isLast ? (
								<Typography key={cacheKey} sx={{ color: 'white !important', fontWeight: 600 }}>
									{label}
								</Typography>
							) : (
								<Box
									key={cacheKey}
									sx={{
										cursor: 'pointer',
										color: 'white !important',
										fontWeight: 500,
										'&:hover': { color: '#90CAF9' }
									}}
									onClick={() => {
										setBreadcrumbPath(newPath);
										if (idx === 0) {
											setSelectedAccount(label);
											setContainerName('');
											setSelectedContainer('');
											setSelectedFolderName('');
										} else if (idx === 1) {
											setSelectedContainer(label);
											setSelectedFolderName('');
										} else if (idx === 2) {
											setSelectedFolderName(label);
										}

										const cached = cache[cacheKey];
										if (cached) {
											setTopStorageAccountsData(transformData(cached.data, conversionUnit));
											setTableDataList(cached.data ?? []);
											setPaginationData({
												totalElements: cached?.pagination?.totalRecords,
												totalPages: cached?.pagination?.totalPages
											});
											setPage(cached?.pagination?.currentPage);
											setPageSize(cached?.pagination?.pageSize);
											setTotalPages(cached?.pagination?.totalPages);
											fetchGrowthTrendData(newPath, projectFilter, sectorFilter);
										} else {
											fetchTableData(newPath, 1, pageSize, projectFilter, sectorFilter, sortingInfo);
											fetchGrowthTrendData(newPath, projectFilter, sectorFilter);
										}
									}}
								>
									{label}
								</Box>
							);
						})}
					</Breadcrumbs>
				</Box>
			)}
			<Grid container spacing={2} px={{ xs: 1, md: 2, lg: 3 }}>
				<Grid item xs={12} md={12} lg={12}>
					{breadcrumbPath.length > 0 && (
						<Box
							sx={{
								mt: 7,
								backgroundColor: '#043362',
								borderRadius: '5px',
								padding: 2,
								color: 'white'
							}}
						>
							<Grid container spacing={4}>
								<Grid item>
									<Typography fontSize="14px" fontWeight={500} color="#A9ADCB">
										Storage Account
									</Typography>
									<Typography fontSize="14px" fontWeight={600} color="#F5F5F5">
										{breadcrumbPath[0]}
									</Typography>
								</Grid>
							</Grid>
						</Box>
					)}
				</Grid>
				{breadcrumbPath.length < 1 && (
					<Grid item xs={12} md={12} lg={3} id="total-storage">
						<DoughnutChartWidget
							title={<HighlightedText text="Total Storage By Sectors" />}
							totalStorage={totalStorage}
							seriesData={storageBySectorsData}
							isLoading={isDoughnutLoading}
						/>
					</Grid>
				)}
				<Grid item xs={12} md={12} lg={breadcrumbPath.length < 1 ? 9 : 12} id="storage-growth-trend">
					<Box
						sx={{
							backgroundColor: '#003771',
							display: 'flex',
							flexDirection: 'row',
							borderRadius: '5px',
							pb: 2
						}}
					>
						{breadcrumbPath.length < 1 ? (
							<StackedBarLineTrendChart
								title={<HighlightedText text="Storage Growth Trend" />}
								data={growthTrendData}
								colorMapping={sectorColors}
								isLoading={growthChartLoading}
								filter={sectorFilter}
							/>
						) : (
							<StackedBarLineTrendChart
								title={<HighlightedText text={'Growth Trend - ' + breadcrumbPath[breadcrumbPath.length - 1]} />}
								data={growthTrendData}
								isLoading={growthChartLoading}
							/>
						)}
					</Box>
				</Grid>

				<Grid item xs={12} id="top-storage-accounts">
					<Stack sx={{ backgroundColor: activeTab === 'Chart' ? '#003771' : '', p: 2, borderRadius: '5px' }}>
						<Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
							<Typography variant="h6" fontWeight={600} color={activeTab === 'Chart' ? '' : `${theme.palette.textLight.main}`}>
								{<HighlightedText text={getChartTitle()} />}
							</Typography>
						</Stack>

						<Grid item xs={12}>
							<Box sx={{ background: '#003771', padding: 2, borderRadius: 2 }}>
								<Box display="flex" justifyContent="space-between" alignItems="center" mt={2} mb={2}>
									<Typography color="white" variant="h6" gutterBottom>
										Filters
									</Typography>
									<Box display="flex" gap={2}>
										<Button
											variant="outlined"
											color="secondary"
											disabled={!isClearEnabled}
											sx={{ color: '#009BD8', border: '009BD8' }}
											onClick={clearFilters}
										>
											CLEAR
										</Button>
										<Button
											variant="outlined"
											color="secondary"
											disabled={!isRunQueryEnabled}
											sx={{ color: '#009BD8', border: '009BD8' }}
											onClick={handleRunQueryClick}
										>
											RUN QUERY
										</Button>
									</Box>
								</Box>
								<Grid container spacing={2} alignItems="center">
									<Grid item xs={6}>
										<TextField
											select
											label="Storage Account Name"
											variant="outlined"
											size="small"
											value={storageAccountName}
											onChange={handleStorageChange}
											sx={{
												flex: 1,
												input: { color: 'white' },
												'& .MuiOutlinedInput-root': {
													'& fieldset': { borderColor: '#A9ADCB' },
													'&:hover fieldset': { borderColor: '#A9ADCB' },
													'&.Mui-focused fieldset': { borderColor: '#A9ADCB' }
												},
												'& label': { color: '#A9ADCB' }
											}}
											InputProps={{ style: { color: 'white' } }}
											fullWidth
										>
											{loadingStorageAccounts ? (
												<MenuItem disabled>
													<LoaderComponent />
												</MenuItem>
											) : storageOptions.length === 0 ? (
												<MenuItem disabled>No storage accounts available</MenuItem>
											) : (
												[...storageOptions].sort((a, b) => a.localeCompare(b)).map((name) => (
													<MenuItem key={name} value={name}>
														{name}
													</MenuItem>
												))
											)}
										</TextField>
									</Grid>

									<Grid item xs={6}>
										<TextField
											select
											label="Container Name"
											variant="outlined"
											size="small"
											value={containerName}
											onChange={(e) => setContainerName(e.target.value)}
											sx={{
												input: { color: 'white' },
												'& .MuiOutlinedInput-root': {
													'& fieldset': { borderColor: '#A9ADCB' },
													'&:hover fieldset': { borderColor: '#A9ADCB' },
													'&.Mui-focused fieldset': { borderColor: '#A9ADCB' }
												},
												'& label': { color: '#A9ADCB' }
											}}
											InputProps={{ style: { color: 'white' } }}
											fullWidth
											disabled={!storageAccountName}
										>
											{loadingContainers ? (
												<MenuItem disabled>
													<LoaderComponent />
												</MenuItem>
											) : availableContainers.length === 0 ? (
												<MenuItem disabled>No containers available</MenuItem>
											) : (
												[...availableContainers].sort((a, b) => a.localeCompare(b)).map((container) => (
													<MenuItem key={container} value={container}>
														{container}
													</MenuItem>
												))
											)}
										</TextField>
									</Grid>
								</Grid>
							</Box>

							<Stack
								direction="row"
								gap={2}
								justifyContent="flex-end"
								alignItems="center"
								marginRight={2}
								marginTop={2}
							>
								<Typography
									sx={{
										cursor: 'pointer',
										fontSize: '16px',
										fontWeight: activeTab === 'Chart' ? 600 : 400,
										color: activeTab === 'Chart' ? '#009BD8' : '#A9ADCB',
										borderBottom: activeTab === 'Chart' ? '3px solid #009BD8' : 'none'
									}}
									onClick={() => {
										handleTabClick('Chart');
									}}
								>
									Chart
								</Typography>
								<Typography
									sx={{
										cursor: 'pointer',
										fontSize: '16px',
										fontWeight: activeTab === 'Table' ? 600 : 400,
										color: activeTab === 'Table' ? '#009BD8' : '#A9ADCB',
										borderBottom: activeTab === 'Table' ? '3px solid #009BD8' : 'none'
									}}
									onClick={() => {
										handleTabClick('Table');
									}}
								>
									Table
								</Typography>
							</Stack>

							{chartView && (
								<ProgressHorizontalBarChart
									isLoading={topStorageChartLoading}
									data={topStorageAccountsData.slice(0, 20)}
									handleEvent={handleBarClick}
								/>
							)}
							{!chartView &&
								(tableLoading ? (
									<LoaderComponent pt={10} />
								) : (
									<CustomizedDataGrid
										title={<HighlightedText text="Query Results" />}
										tableType={
											breadcrumbPath.length === 0
												? 'storage-accounts-list'
												: breadcrumbPath.length === 1
													? 'containers-list'
													: 'folders-list'
										}
										tableColumns={getColumns()}
										tableData={tableDataList}
										defaultColumnNames={defaultColumnNames}
										onSorting={handleSorting}
										currSorting={sortingInfo}
										page={page}
										pageSize={pageSize}
										paginationData={paginationData}
										listLength={tableDataList?.length}
										handlepageSizeChange={handlepageSizeChange}
										handlePageChange={handlePageChange}
										isDateRangeFilter={false}
										isDisabled={false}
										isFetching={loading}
										childRowClass={true}
										isShowMoreButton={true}
										isSortIcon={true}
										showExport={true}
										exportTableData={() =>
											exportTableData(
												breadcrumbPath,
												page,
												pageSize,
												projectFilter,
												sectorFilter,
												conversionUnit,
												sortingInfo
											)
										}
										isLighterTheme={true}
									/>
								))}
						</Grid>
					</Stack>
				</Grid>
			</Grid>
		</PageWrapper>
	);
};

export default azureStorage;
